import { Box, Button, Typography } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateRuleRulesPostMutation } from "api";
import { SelectInput, SwitchInput, TextInput } from "components/forms";
import { IRuleAwake } from "interfaces/rules.interface";

import { RulePresetAwake as config } from "../common/RulePresetMinMaxValues";
import { configurationSchema, timeRestrictionSchema } from "../common/schemas";
import { TimeRestrictionInput } from "../components/TimeRestrictionInput";

const conditionsSchema = z
  .object({
    initial_presence_time_epochs_gte: z.coerce
      .number()
      .min(config.initial_presence_time_epochs_gte.exclusiveMinimum)
      .max(config.initial_presence_time_epochs_gte.exclusiveMaximum)
      .default(config.initial_presence_time_epochs_gte.default),
    ok_to_alert: z.object({
      awake_time_epochs_gte: z.coerce
        .number()
        .min(config.awake_time_epochs_gte.exclusiveMinimum)
        .max(config.awake_time_epochs_gte.exclusiveMaximum)
        .default(config.awake_time_epochs_gte.default),
    }),
    alert_to_ok: z.object({
      asleep_time_epochs_gte: z.coerce
        .number()
        .min(config.asleep_time_epochs_gte.exclusiveMinimum)
        .max(config.asleep_time_epochs_gte.exclusiveMaximum)
        .default(config.asleep_time_epochs_gte.default),
    }),
  })
  .nullable()
  .default(null);

const ruleAwakeSchema = z.object({
  time_restriction: timeRestrictionSchema,
  conditions_preset: z.enum(["default", ""]).nullable().default("default"),
  conditions: conditionsSchema,
  configuration: configurationSchema,
});

type RuleAwakeSchema = z.infer<typeof ruleAwakeSchema>;

type RuleAwakeFormProps = {
  entityId: string;
  presets: { id: string; name: string }[];
  defaultValues: IRuleAwake | null;
  disabled: boolean;
  onClose: () => void;
};

export function RuleAwakeForm({
  entityId,
  presets,
  defaultValues,
  disabled,
  onClose,
}: RuleAwakeFormProps) {
  const { t } = useTranslation();

  const [createRule] = useCreateRuleRulesPostMutation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: ruleAwakeSchema.safeParse(defaultValues ?? {}).data,
    resolver: zodResolver(ruleAwakeSchema),
  });

  const onSubmit = async (data: RuleAwakeSchema) => {
    await createRule({
      ruleCreateModels: {
        ...data,
        type: "subject.session.epochs.awake",
        entity_id: entityId,
        description: null,
        enabled: true,
        conditions_preset: data.conditions_preset || null,
      },
    });

    onClose();
  };

  const presetId = watch("conditions_preset");

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmit, console.error)}
      sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}
    >
      <SelectInput
        control={control}
        label={t("preset")}
        fieldName={"conditions_preset"}
        options={presets}
        placeholder={t("custom")}
        optionLabelField={"name"}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value.length === 0) {
            const value = conditionsSchema.parse({
              ok_to_alert: {},
              alert_to_ok: {},
            });

            setValue("conditions", value);
          } else {
            setValue("conditions", null);
          }
        }}
      />
      {!presetId && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: 1,
            border: 1,
            borderRadius: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant={"bodyLargeMedium"}>
            {t("send_notification_message")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.initial_presence_time_epochs_gte"}
              label={t("minimum_time_presence_is")}
              type={"number"}
              unit={t("mins")}
              min={config.initial_presence_time_epochs_gte.exclusiveMinimum}
              max={config.initial_presence_time_epochs_gte.exclusiveMaximum}
            />
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.ok_to_alert.awake_time_epochs_gte"}
              label={t("and") + " " + t("time_awake_is")}
              type={"number"}
              unit={t("mins")}
              min={config.awake_time_epochs_gte.exclusiveMinimum}
              max={config.awake_time_epochs_gte.exclusiveMaximum}
            />
          </Box>
          <Typography variant={"bodyLargeMedium"}>
            {t("send_ok_message_only_through_SMS")}
          </Typography>
          <Box
            sx={{
              padding: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <SwitchInput
              control={control}
              disabled={disabled}
              fieldName={"configuration.execute_actions_on_alert_to_ok"}
              label={t("Enabled")}
            />
            <TextInput
              control={control}
              disabled={disabled}
              fieldName={"conditions.alert_to_ok.asleep_time_epochs_gte"}
              label={t("time_asleep_is_greater_than")}
              type={"number"}
              unit={t("mins")}
              min={config.asleep_time_epochs_gte.exclusiveMinimum}
              max={config.asleep_time_epochs_gte.exclusiveMaximum}
            />
          </Box>
        </Box>
      )}
      <TimeRestrictionInput
        control={control}
        setValue={setValue}
        disabled={disabled}
      />
      <Box sx={{ flexGrow: 1 }} />
      {!disabled && <Button type={"submit"}>{t("Create")}</Button>}
    </Box>
  );
}
